import moment from 'moment';

/** ApexCharts uses multiple tspans when `text` is string[] — `\n` alone does not wrap x-axis labels. */
export type BarChartPeriodAxisLabel = string | string[];

export type RollingPeriodRow = {
  from?: string | null;
  end: string;
};

/**
 * Label for one rolling 12-month bar.
 *
 * The period start comes from the API (`from`), chained to the previous period's end.
 * It must not be computed as "end minus one year": Axxion rolls on the fund's valuation
 * calendar, so that arithmetic is wrong whenever a holiday or month-end anchor is involved
 * (e.g. the period ending 29.07.2022 starts 30.07.2021, not 29.07.2021).
 */
export function rollingPeriodLabel(item: RollingPeriodRow): string {
  const from = item.from?.trim();
  return from ? `${from} bis ${item.end}` : item.end;
}

export function buildRollingPeriodLabels(rows: RollingPeriodRow[]): string[] {
  return rows.map(rollingPeriodLabel);
}

/** @deprecated Use rollingPeriodLabel — end−1 year cannot reproduce Axxion valuation calendar. */
export function buildBarChartPeriodLabel(end: string): string {
  const toDate = moment(end, 'DD.MM.YYYY', true);
  if (!toDate.isValid()) return end;
  const fromDate = toDate.clone().subtract(1, 'year');
  return `${fromDate.format('DD.MM.YYYY')} bis ${toDate.format('DD.MM.YYYY')}`;
}

/** @deprecated Use buildRollingPeriodLabels with API rows. */
export function buildBarChartPeriodLabels(ends: string[]): string[] {
  return ends.map((end) => buildBarChartPeriodLabel(end));
}

/** @deprecated Use buildRollingPeriodLabels. */
export function buildChainedBarChartPeriodLabels(ends: string[]): string[] {
  return buildBarChartPeriodLabels(ends);
}

/**
 * Compact period labels for narrow columns: split `DD.MM.YY bis DD.MM.YY` into three lines.
 */
export function formatBarChartPeriodLabel(label: string): BarChartPeriodAxisLabel {
  const trimmed = (label || '').trim();
  if (!trimmed) return label;

  if (!/\bbis\b/i.test(trimmed)) {
    if (!/\s/.test(trimmed)) {
      const compact = trimmed.match(/^(.+?\d{4})(.+)$/);
      if (compact) return [compact[1].trim(), compact[2].trim()];
    }
    return trimmed;
  }

  const formatShortDate = (value: string): string => {
    const m = moment(value.trim(), ['DD.MM.YYYY', 'DD.MM.YY'], true);
    return m.isValid() ? m.format('DD.MM.YY') : value.trim();
  };

  const parts = trimmed.split(/\s+bis\s+/i).map((p) => p.trim());
  if (parts.length === 2) {
    return [formatShortDate(parts[0]), 'bis', formatShortDate(parts[1])];
  }
  return trimmed;
}

export const GROSS_ROLLING_BAR_TYPE = 'gross_perf_year_as_of_today';
export const NET_ROLLING_BAR_TYPE = 'perf_year_as_of_today';
const ROLLING_BAR_TYPES = [GROSS_ROLLING_BAR_TYPE, NET_ROLLING_BAR_TYPE] as const;

export type AutomatedBarChartApiRow = RollingPeriodRow & {
  type: string;
  value: number;
};

export type AlignedBarChartSeries = {
  name: string;
  data: (number | null)[];
  color?: string;
};

const sortRowsByEnd = (rows: AutomatedBarChartApiRow[]): AutomatedBarChartApiRow[] =>
  [...rows].sort(
    (a, b) =>
      moment(a.end, 'DD.MM.YYYY', true).valueOf() -
      moment(b.end, 'DD.MM.YYYY', true).valueOf(),
  );

/** Align gross/net series to the same period-end order (gross ends define categories). */
export function buildAlignedAutomatedBarChartSeries(
  apiData: AutomatedBarChartApiRow[],
  options: {
    legend?: Record<string, string>;
    colors?: string[];
    fallbackColor?: string;
  } = {},
): { labels: string[]; series: AlignedBarChartSeries[] } {
  const valueByTypeEnd = new Map<string, number>();
  for (const row of apiData) {
    valueByTypeEnd.set(`${row.type}|${row.end}`, row.value);
  }

  const grossRows = sortRowsByEnd(
    apiData.filter((row) => row.type === GROSS_ROLLING_BAR_TYPE),
  );

  const categoryRows =
    grossRows.length > 0
      ? grossRows
      : sortRowsByEnd(
          Array.from(
            apiData.reduce((map, row) => {
              if (!map.has(row.end)) map.set(row.end, row);
              return map;
            }, new Map<string, AutomatedBarChartApiRow>()).values(),
          ),
        );

  const ends = categoryRows.map((row) => row.end);
  const labels = buildRollingPeriodLabels(categoryRows);

  const typesPresent = ROLLING_BAR_TYPES.filter((barType) =>
    apiData.some((row) => row.type === barType),
  );

  const formatSeriesName = (barType: string) => {
    const fromLegend = options.legend?.[barType]?.trim();
    if (fromLegend) return fromLegend;
    if (barType === GROSS_ROLLING_BAR_TYPE) return 'Gross';
    if (barType === NET_ROLLING_BAR_TYPE) return 'Net';
    return barType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const series = typesPresent.map((barType, index) => ({
    name: formatSeriesName(barType),
    data: ends.map((end) => valueByTypeEnd.get(`${barType}|${end}`) ?? null),
    color: options.colors?.[index] ?? options.fallbackColor,
  }));

  return { labels, series };
}
